import { c as _c } from "react/compiler-runtime";
import type { MutableRefObject, PointerEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { BsPin, BsPinFill, BsPlayFill } from "react-icons/bs";
import { FiArrowUpRight, FiCheck, FiClipboard, FiCopy, FiEdit, FiEye, FiFileText, FiLink, FiPrinter, FiTrash2 } from "react-icons/fi";
import { TbFileArrowRight } from "react-icons/tb";
import SuperbillNoteDialog from "@/components/billing/superbill-note-dialog";
import { NoteCompletionMenuItem } from "@/components/notes/note-completion-menu-item";
import { useUser } from "@/contexts/user-provider";
import { useDuplicateNote } from "@/hooks/use-duplicate-note";
import { useNotePrintFlow } from "@/hooks/use-note-print-flow";
import { useSignOffWithSignatureGuard } from "@/hooks/use-signoff-with-signature-guard";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { toast } from "@/lib/use-toast";
import { NoteListItem } from "@/types/notes";
import DeleteConfirmationModal from "./delete-confirmation-modal";
import NoteViewDialog from "./patient/notes/note-view-dialog";
import { useProgressNavigation } from "./progress-bar";
import LinkNoteToAppointmentDialog from "./scribe/link-note-to-appointment-dialog";
import { NotePrintDialogHost } from "./scribe/note-print-dialog-host";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger, DropdownMenuTrigger, DropdownSettingsButton } from "./ui/dropdown";
import { useDocumentTemplatesQuery, useGenerateDocumentMutation } from "@/api/provider/generated-document/generated-document-query";
import { useArchiveNoteMutation, useTogglePinNoteMutation } from "@/api/provider/scribe/note-mutation";
import { useScribeRecording } from "@/stores/scribe-store";

/**
 * For the element wrapping a NoteDropdown in a clickable row: stops only the
 * pointer-downs that start inside that element. The dropdown's dialogs and menus
 * are portalled but still bubble through it in React, and stopping theirs also
 * stops the native event before Radix's document listener sees it — so the
 * View dialog's photo viewer and its "..." menu could never close on a click
 * outside, leaving the screen stuck until a reload.
 */
export function stopOwnPointerDown(e: PointerEvent<HTMLElement>) {
  if (e.currentTarget.contains(e.target as Node)) e.stopPropagation();
}
export default function NoteDropdown(t0) {
  const $ = _c(110);
  const {
    note,
    className,
    suppressNextRowClickRef
  } = t0;
  const {
    userHasAiScribeUsePermission,
    currentRecordingId,
    handleResumeRecording
  } = useScribeRecording();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    currentUser
  } = useUser();
  const printFlow = useNotePrintFlow();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const archiveMutation = useArchiveNoteMutation();
  const {
    requestSignOff,
    warningDialog: signOffWarningDialog,
    isPending: isSigningOff
  } = useSignOffWithSignatureGuard();
  let t1;
  if ($[0] !== currentUser || $[1] !== note.requiresCoSign || $[2] !== note.signOff || $[3] !== note.signedOffProviderId) {
    t1 = hasPermission(currentUser, PERMISSIONS.SIGN_OFF_NOTES) && (note.signOff ? !note.signedOffProviderId && note.signOff.viewerCanSign || !!note.requiresCoSign && note.signOff.viewerCanCoSign : !note.signedOffProviderId || !!note.requiresCoSign && !!currentUser?.isPrescriber);
    $[0] = currentUser;
    $[1] = note.requiresCoSign;
    $[2] = note.signOff;
    $[3] = note.signedOffProviderId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const canSignFromMenu = t1;
  const pinMutation = useTogglePinNoteMutation();
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteViewDialogOpen, setNoteViewDialogOpen] = useState(false);
  const [linkAppointmentOpen, setLinkAppointmentOpen] = useState(false);
  const [superbillOpen, setSuperbillOpen] = useState(false);
  const duplicateNoteMutation = useDuplicateNote();
  const generateDocumentMutation = useGenerateDocumentMutation();
  let t2;
  if ($[5] !== currentUser) {
    t2 = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
    $[5] = currentUser;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data: documentTemplates
  } = useDocumentTemplatesQuery(t2);
  let t3;
  if ($[7] !== generateDocumentMutation || $[8] !== navigateWithProgress || $[9] !== note.id) {
    t3 = templateId => {
      generateDocumentMutation.mutate({
        noteId: note.id,
        templateId
      }, {
        onSuccess: plan => {
          if (plan) {
            navigateWithProgress(`/treatment-plans/${plan.id}`);
          }
        },
        onError: _temp
      });
    };
    $[7] = generateDocumentMutation;
    $[8] = navigateWithProgress;
    $[9] = note.id;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleGenerateDocument = t3;
  const suppressClearTimeoutRef = useRef(null);
  let t4;
  if ($[11] !== suppressNextRowClickRef) {
    t4 = () => {
      if (!suppressNextRowClickRef) {
        return;
      }
      suppressNextRowClickRef.current = true;
      if (suppressClearTimeoutRef.current !== null) {
        clearTimeout(suppressClearTimeoutRef.current);
      }
      suppressClearTimeoutRef.current = setTimeout(() => {
        suppressNextRowClickRef.current = false;
        suppressClearTimeoutRef.current = null;
      }, 750);
    };
    $[11] = suppressNextRowClickRef;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const armSuppressForGhostClick = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = note_0 => {
      setSelectedNote(note_0);
      setNoteViewDialogOpen(true);
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleViewNote = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = window.location.pathname.includes("/check-in/calendar");
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const onCalendarPage = t6;
  let t7;
  if ($[15] !== className) {
    t7 = <DropdownMenuTrigger asChild={true}><DropdownSettingsButton aria-label="Actions" className={className} /></DropdownMenuTrigger>;
    $[15] = className;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = suppressNextRowClickRef ? armSuppressForGhostClick : undefined;
  let t9;
  if ($[17] !== armSuppressForGhostClick || $[18] !== suppressNextRowClickRef) {
    t9 = suppressNextRowClickRef ? e => {
      e.preventDefault();
      armSuppressForGhostClick();
    } : undefined;
    $[17] = armSuppressForGhostClick;
    $[18] = suppressNextRowClickRef;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== armSuppressForGhostClick || $[21] !== currentRecordingId || $[22] !== currentUser || $[23] !== documentTemplates || $[24] !== duplicateNoteMutation || $[25] !== generateDocumentMutation.isPending || $[26] !== handleGenerateDocument || $[27] !== handleResumeRecording || $[28] !== navigateWithProgress || $[29] !== note || $[30] !== printFlow || $[31] !== userHasAiScribeUsePermission) {
    t10 = note.aiGenerated && !note.hasAnswers ? <DropdownMenuItem disabled={!userHasAiScribeUsePermission || currentRecordingId !== undefined} onSelect={e_0 => {
      e_0.preventDefault();
      armSuppressForGhostClick();
      handleResumeRecording(note);
    }} icon={<BsPlayFill className="h-4 w-4" />}>Resume</DropdownMenuItem> : <><DropdownMenuItem onSelect={e_1 => {
        e_1.preventDefault();
        armSuppressForGhostClick();
        navigateWithProgress(`/scribe/playground/${note.id}`);
      }} icon={<FiEdit className="h-4 w-4" />}>Edit</DropdownMenuItem><DropdownMenuItem onSelect={e_2 => {
        e_2.preventDefault();
        handleViewNote(note);
      }} icon={<FiEye className="h-4 w-4" />}>View</DropdownMenuItem><DropdownMenuItem disabled={!note.hasAnswers || printFlow.state.isPreparing} onSelect={e_3 => {
        e_3.preventDefault();
        printFlow.actions.requestPrint(note);
      }} icon={<FiPrinter className="h-4 w-4" />}>{printFlow.state.isPreparing ? "Preparing..." : "Print"}</DropdownMenuItem><DropdownMenuItem disabled={duplicateNoteMutation.isPending} onSelect={e_4 => {
        e_4.preventDefault();
        duplicateNoteMutation.mutate(note.id);
      }} icon={<FiCopy className="h-4 w-4" />}>{duplicateNoteMutation.isPending ? "Duplicating..." : "Duplicate"}</DropdownMenuItem>{hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE) && <><DropdownMenuItem disabled={!note.hasAnswers || generateDocumentMutation.isPending} onSelect={e_5 => {
          e_5.preventDefault();
          handleGenerateDocument();
        }} icon={<FiClipboard className="h-4 w-4" />}>{generateDocumentMutation.isPending ? "Generating..." : "Generate treatment plan"}</DropdownMenuItem>{documentTemplates && documentTemplates.length > 0 && <DropdownMenuSubMenu><DropdownMenuSubMenuTrigger disabled={!note.hasAnswers || generateDocumentMutation.isPending}><FiClipboard className="mr-2 h-4 w-4" />From template</DropdownMenuSubMenuTrigger><DropdownMenuSubMenuContent className="max-h-80 overflow-y-auto">{documentTemplates.map(tpl => <DropdownMenuItem key={tpl.id} onSelect={e_6 => {
              e_6.preventDefault();
              handleGenerateDocument(tpl.id);
            }}>{tpl.name}</DropdownMenuItem>)}</DropdownMenuSubMenuContent></DropdownMenuSubMenu>}</>}</>;
    $[20] = armSuppressForGhostClick;
    $[21] = currentRecordingId;
    $[22] = currentUser;
    $[23] = documentTemplates;
    $[24] = duplicateNoteMutation;
    $[25] = generateDocumentMutation.isPending;
    $[26] = handleGenerateDocument;
    $[27] = handleResumeRecording;
    $[28] = navigateWithProgress;
    $[29] = note;
    $[30] = printFlow;
    $[31] = userHasAiScribeUsePermission;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== note.id || $[34] !== pinMutation) {
    t11 = e_7 => {
      e_7.preventDefault();
      pinMutation.mutate({
        noteId: note.id
      });
    };
    $[33] = note.id;
    $[34] = pinMutation;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== note.isPinned) {
    t12 = note.isPinned ? <BsPinFill className="h-4 w-4" /> : <BsPin className="h-4 w-4" />;
    $[36] = note.isPinned;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const t13 = note.isPinned ? "Unpin Note" : "Pin Note";
  let t14;
  if ($[38] !== t11 || $[39] !== t12 || $[40] !== t13) {
    t14 = <DropdownMenuItem onSelect={t11} icon={t12}>{t13}</DropdownMenuItem>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const t15 = !!note.signedOffProviderId;
  let t16;
  if ($[42] !== note.completedDate || $[43] !== note.id || $[44] !== t15) {
    t16 = <NoteCompletionMenuItem noteId={note.id} completedDate={note.completedDate} isSignedOff={t15} />;
    $[42] = note.completedDate;
    $[43] = note.id;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== canSignFromMenu || $[47] !== isSigningOff || $[48] !== note.id || $[49] !== note.signedOffProviderId || $[50] !== requestSignOff) {
    t17 = canSignFromMenu && <DropdownMenuItem disabled={isSigningOff} onSelect={e_8 => {
      e_8.preventDefault();
      requestSignOff(note.id);
    }} icon={<FiCheck className="h-4 w-4" />}>{note.signedOffProviderId ? "Co-sign" : "Sign Off"}</DropdownMenuItem>;
    $[46] = canSignFromMenu;
    $[47] = isSigningOff;
    $[48] = note.id;
    $[49] = note.signedOffProviderId;
    $[50] = requestSignOff;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== currentUser) {
    t18 = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE) && <DropdownMenuItem icon={<FiLink className="h-4 w-4" />} onSelect={e_9 => {
      e_9.preventDefault();
      setLinkAppointmentOpen(true);
    }}>Link appointment</DropdownMenuItem>;
    $[52] = currentUser;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== navigateWithProgress || $[55] !== note.eventId) {
    t19 = note.eventId && !onCalendarPage && <DropdownMenuItem icon={<FiArrowUpRight className="h-4 w-4" />} onSelect={e_10 => {
      e_10.preventDefault();
      navigateWithProgress(`/check-in/calendar/?eventId=${note.eventId}`);
    }}>View Appointment</DropdownMenuItem>;
    $[54] = navigateWithProgress;
    $[55] = note.eventId;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== currentUser) {
    t20 = hasPermission(currentUser, PERMISSIONS.BILLING_INVOICES_WRITE) && hasPermission(currentUser, PERMISSIONS.BILLING_READ) && <DropdownMenuItem icon={<FiFileText className="h-4 w-4" />} onSelect={e_11 => {
      e_11.preventDefault();
      setSuperbillOpen(true);
    }}>Link Super Bill</DropdownMenuItem>;
    $[57] = currentUser;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== currentUser || $[60] !== navigateWithProgress || $[61] !== note.superbillId) {
    t21 = note.superbillId && hasPermission(currentUser, PERMISSIONS.BILLING_READ) && <DropdownMenuItem icon={<TbFileArrowRight className="h-4 w-4" />} onSelect={e_12 => {
      e_12.preventDefault();
      navigateWithProgress(`/billing/superbills/${note.superbillId}`);
    }}>Go to Super Bill</DropdownMenuItem>;
    $[59] = currentUser;
    $[60] = navigateWithProgress;
    $[61] = note.superbillId;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== currentUser || $[64] !== note) {
    t22 = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_ARCHIVE) && <><DropdownMenuSeparator /><DropdownMenuItem onSelect={e_13 => {
        e_13.preventDefault();
        setSelectedNote(note);
        setIsDeleteModalOpen(true);
      }} className="text-status-error-text" icon={<FiTrash2 className="h-4 w-4 text-status-error-text" />}>Delete</DropdownMenuItem></>;
    $[63] = currentUser;
    $[64] = note;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== t10 || $[67] !== t14 || $[68] !== t16 || $[69] !== t17 || $[70] !== t18 || $[71] !== t19 || $[72] !== t20 || $[73] !== t21 || $[74] !== t22 || $[75] !== t8 || $[76] !== t9) {
    t23 = <DropdownMenuContent align="end" onPointerDownCapture={t8} onCloseAutoFocus={t9}>{t10}{t14}{t16}{t17}{t18}{t19}{t20}{t21}{t22}</DropdownMenuContent>;
    $[66] = t10;
    $[67] = t14;
    $[68] = t16;
    $[69] = t17;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
    $[73] = t21;
    $[74] = t22;
    $[75] = t8;
    $[76] = t9;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  let t24;
  if ($[78] !== t23 || $[79] !== t7) {
    t24 = <DropdownMenu>{t7}{t23}</DropdownMenu>;
    $[78] = t23;
    $[79] = t7;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] !== printFlow) {
    t25 = <NotePrintDialogHost printFlow={printFlow} />;
    $[81] = printFlow;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== noteViewDialogOpen || $[84] !== selectedNote) {
    t26 = <NoteViewDialog isOpen={noteViewDialogOpen} onOpenChange={setNoteViewDialogOpen} note={selectedNote} />;
    $[83] = noteViewDialogOpen;
    $[84] = selectedNote;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== archiveMutation || $[87] !== selectedNote) {
    t27 = () => {
      if (!selectedNote) {
        return;
      }
      archiveMutation.mutate({
        noteId: selectedNote.id
      });
    };
    $[86] = archiveMutation;
    $[87] = selectedNote;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== isDeleteModalOpen || $[90] !== t27) {
    t28 = <DeleteConfirmationModal title="Delete Note" subtitle="Are you sure you want to delete this note? This action cannot be undone." isOpen={isDeleteModalOpen} setIsOpen={setIsDeleteModalOpen} onConfirm={t27} />;
    $[89] = isDeleteModalOpen;
    $[90] = t27;
    $[91] = t28;
  } else {
    t28 = $[91];
  }
  let t29;
  if ($[92] !== linkAppointmentOpen || $[93] !== note.eventId || $[94] !== note.id || $[95] !== note.patient.id) {
    t29 = <LinkNoteToAppointmentDialog open={linkAppointmentOpen} onOpenChange={setLinkAppointmentOpen} noteId={note.id} patientId={note.patient.id} currentEventId={note.eventId} />;
    $[92] = linkAppointmentOpen;
    $[93] = note.eventId;
    $[94] = note.id;
    $[95] = note.patient.id;
    $[96] = t29;
  } else {
    t29 = $[96];
  }
  let t30;
  if ($[97] !== note.eventId || $[98] !== note.id || $[99] !== note.patient.id || $[100] !== superbillOpen) {
    t30 = <SuperbillNoteDialog open={superbillOpen} onOpenChange={setSuperbillOpen} noteId={note.id} patientId={note.patient.id} eventId={note.eventId} />;
    $[97] = note.eventId;
    $[98] = note.id;
    $[99] = note.patient.id;
    $[100] = superbillOpen;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== signOffWarningDialog || $[103] !== t24 || $[104] !== t25 || $[105] !== t26 || $[106] !== t28 || $[107] !== t29 || $[108] !== t30) {
    t31 = <>{t24}{t25}{t26}{t28}{t29}{t30}{signOffWarningDialog}</>;
    $[102] = signOffWarningDialog;
    $[103] = t24;
    $[104] = t25;
    $[105] = t26;
    $[106] = t28;
    $[107] = t29;
    $[108] = t30;
    $[109] = t31;
  } else {
    t31 = $[109];
  }
  return t31;
}
async function _temp(error) {
  toast({
    title: "Couldn't create treatment plan",
    description: await parseError(error),
    variant: "error"
  });
}